// Create and edit a reminder: one line, an optional date, and an optional Notes
// area. A reminder is still one routine row. Its note is `routine.note`, so the
// sheet and the Notes tab edit the same value through the same routine route.

import { useState } from 'react';
import { api } from '../api/client.ts';
import type { IsoDay, Routine, RoutinePatch } from '../api/client.ts';
import { addDays } from '../iso-day.ts';
import { Field, FieldGroup, Notice, Sheet, Stuck, useOnce, Waiting } from '../edit/Sheet.tsx';
import type { NewReminderInput } from './reminders-model.ts';

interface NewProps {
  /**
   * The account's today, which is what `Today` and `Tomorrow` mean. Null while
   * it is unknown, and then those two chips are not offered rather than being
   * worked out here - no client decides the account's day (AGENTS.md). Picking
   * a day by hand still works, so the field is never a dead end.
   */
  today: IsoDay | null;
  busy: boolean;
  problem: string | null;
  onCreate: (input: NewReminderInput) => void;
  onClose: () => void;
}

/** Which of the three quick answers is chosen, or a day picked by hand. */
type When = 'none' | 'today' | 'tomorrow' | 'pick';

function initialWhen(dueDay: IsoDay | null, today: IsoDay | null): When {
  if (dueDay === null) return 'none';
  if (today !== null && dueDay === today) return 'today';
  if (today !== null && dueDay === addDays(today, 1)) return 'tomorrow';
  return 'pick';
}

function chosenDay(when: When, picked: string, today: IsoDay | null): IsoDay | null {
  if (when === 'today') return today;
  if (when === 'tomorrow' && today !== null) return addDays(today, 1);
  if (when === 'pick' && picked !== '') return picked;
  return null;
}

function WhenFields({
  today,
  when,
  picked,
  disabled,
  onWhen,
  onPicked,
}: {
  today: IsoDay | null;
  when: When;
  picked: string;
  disabled: boolean;
  onWhen: (when: When) => void;
  onPicked: (day: string) => void;
}) {
  const options: { key: When; label: string }[] = [
    { key: 'none', label: 'No date' },
    ...(today === null
      ? []
      : [
          { key: 'today' as const, label: 'Today' },
          { key: 'tomorrow' as const, label: 'Tomorrow' },
        ]),
    { key: 'pick', label: 'Pick a day' },
  ];

  return (
    <>
      <FieldGroup label="When, if it matters">
        <div className="chips">
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={when === option.key}
              disabled={disabled}
              onClick={() => onWhen(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </FieldGroup>

      {/* A real date input rather than a calendar of this client's own: the
          browser's is keyboard-operable, localised and understood by screen
          readers. It appears only after that deeper choice is made. */}
      {when === 'pick' && (
        <Field label="Which day?">
          {(id) => (
            <input
              id={id}
              type="date"
              value={picked}
              disabled={disabled}
              onChange={(event) => onPicked(event.target.value)}
            />
          )}
        </Field>
      )}
    </>
  );
}

/**
 * The optional Notes area. Opening it changes only this in-memory draft: no
 * request is made, and a whitespace-only draft is omitted on create. Once a
 * note exists the area opens with it, so reopening a reminder never hides its
 * writing behind a control that looks like an invitation to make another one.
 */
function ReminderNotes({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(() => value.trim() !== '');

  if (!open) {
    return (
      <div className="field">
        <button
          type="button"
          className="btn"
          aria-expanded="false"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Add notes
        </button>
        <p className="finetext">Optional. Nothing is created until there are words to save.</p>
      </div>
    );
  }

  return (
    <Field label="Notes (optional)">
      {(id) => (
        <textarea
          id={id}
          rows={4}
          placeholder="Anything you want to remember about this one. # makes a heading, - makes a list."
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

function Problem({ problem }: { problem: string | null }) {
  return problem === null ? null : (
    <p className="sheetproblem" role="alert">
      {problem}
    </p>
  );
}

export function ReminderSheet({ today, busy, problem, onCreate, onClose }: NewProps) {
  const [title, setTitle] = useState('');
  const [when, setWhen] = useState<When>('none');
  const [picked, setPicked] = useState('');
  const [note, setNote] = useState('');

  const dueDay = chosenDay(when, picked, today);
  // A picked day that has not been picked yet is not a reminder without a
  // date - it is a form that is not finished.
  const ready = title.trim() !== '' && !(when === 'pick' && picked === '');

  return (
    <Sheet
      title="New reminder"
      onClose={onClose}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={busy || !ready}
            onClick={() =>
              onCreate({
                title: title.trim(),
                dueDay,
                // Preserve meaningful prose exactly as typed. Whitespace alone
                // is an unopened draft as far as persistence is concerned.
                ...(note.trim() === '' ? {} : { note }),
              })
            }
          >
            {busy ? 'Adding…' : 'Add reminder'}
          </button>
        </>
      }
    >
      <h3>New reminder</h3>
      <p className="sub">One thing you need to do. It does not belong to a schedule.</p>

      <Field label="What do you need to do?">
        {(id) => (
          <input
            id={id}
            type="text"
            autoFocus
            autoComplete="off"
            placeholder="Book the dentist"
            value={title}
            disabled={busy}
            onChange={(event) => setTitle(event.target.value)}
          />
        )}
      </Field>

      <WhenFields
        today={today}
        when={when}
        picked={picked}
        disabled={busy}
        onWhen={setWhen}
        onPicked={setPicked}
      />
      <ReminderNotes value={note} disabled={busy} onChange={setNote} />
      {busy && (
        <p className="sr" role="status">
          Adding reminder. Its fields are unavailable until the save finishes.
        </p>
      )}

      <Notice>
        Most reminders do not need a date. One that has a date and goes past it is shown as overdue,
        in plain words - it is never counted against you and it never turns red.
      </Notice>
      <Problem problem={problem} />
    </Sheet>
  );
}

interface EditProps {
  reminderId: string;
  today: IsoDay | null;
  busy: boolean;
  conflicted: boolean;
  problem: string | null;
  onSave: (id: string, patch: RoutinePatch) => void;
  onSignedOut: () => void;
  onClose: () => void;
}

/** Fetch before drawing, as the item editor does: a list row is not an edit model. */
export function EditReminderSheet({
  reminderId,
  today,
  busy,
  conflicted,
  problem,
  onSave,
  onSignedOut,
  onClose,
}: EditProps) {
  const routine = useOnce(reminderId, (id) => api.routine(id), onSignedOut);

  if (routine.status === 'loading') return <Waiting title="Edit reminder" onClose={onClose} />;
  if (routine.status === 'failed') {
    return <Stuck title="Edit reminder" message={routine.message} onClose={onClose} />;
  }
  if (routine.value.kind !== 'reminder') {
    return <Stuck title="Edit reminder" message="No such reminder." onClose={onClose} />;
  }

  return (
    <EditReminderForm
      routine={routine.value}
      today={today}
      busy={busy}
      conflicted={conflicted}
      problem={problem}
      onSave={onSave}
      onClose={onClose}
    />
  );
}

function EditReminderForm({
  routine,
  today,
  busy,
  conflicted,
  problem,
  onSave,
  onClose,
}: {
  routine: Routine;
  today: IsoDay | null;
  busy: boolean;
  conflicted: boolean;
  problem: string | null;
  onSave: (id: string, patch: RoutinePatch) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(routine.title);
  const [when, setWhen] = useState<When>(() => initialWhen(routine.dueDay, today));
  const [picked, setPicked] = useState(routine.dueDay ?? '');
  const [note, setNote] = useState(routine.note);

  const dueDay = chosenDay(when, picked, today);
  const ready = title.trim() !== '' && !(when === 'pick' && picked === '');

  const save = () => {
    const storedNote = routine.note.trim() === '' ? '' : routine.note;
    const nextNote = note.trim() === '' ? '' : note;
    onSave(routine.id, {
      title: title.trim(),
      dueDay,
      // Opening the area and leaving it empty writes nothing. Clearing an
      // existing note is different: the empty string deliberately removes it.
      ...(nextNote === storedNote
        ? {}
        : {
            note: nextNote,
            ...(nextNote === ''
              ? {
                  noteClearExpectedNote: storedNote,
                  noteClearExpectedRevision: routine.revision,
                }
              : {}),
          }),
    });
  };

  return (
    <Sheet
      title={routine.title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || conflicted || !ready} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <h3>{routine.title}</h3>
      <Field label="What do you need to do?">
        {(id) => (
          <input
            id={id}
            type="text"
            autoComplete="off"
            value={title}
            disabled={busy}
            onChange={(event) => setTitle(event.target.value)}
          />
        )}
      </Field>
      <WhenFields
        today={today}
        when={when}
        picked={picked}
        disabled={busy}
        onWhen={setWhen}
        onPicked={setPicked}
      />
      <ReminderNotes value={note} disabled={busy} onChange={setNote} />
      {busy && (
        <p className="sr" role="status">
          Saving reminder. Its fields are unavailable until the save finishes.
        </p>
      )}
      <Problem problem={problem} />
    </Sheet>
  );
}
